import { createApi } from '@reduxjs/toolkit/query/react';
import { Endpoint } from 'services/endpoints';

import { HTTPMethod, Response } from 'types/http';

import { getDefaultPagination } from '../../utils/pagination';
import { fetchBaseQueryWithAuth } from '../api';
import { TAGS } from './keys';
import { PayoutInfoResponse, PayoutQuery, PayoutsListRequest, PayoutsListResponse } from './types';

const REDUCER_PATH = 'payoutsApi';

export const payoutsApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQueryWithAuth,
  tagTypes: [TAGS.PAYOUT_LIST, TAGS.SINGLE_PAYOUT],
  endpoints: (builder) => ({
    getPayoutsList: builder.query<PayoutsListResponse, PayoutsListRequest>({
      query: ({ accountUuid, ...requestBody }) => ({
        url: Endpoint.Payouts.List(accountUuid),
        method: HTTPMethod.POST,
        body: requestBody,
      }),
      transformResponse: (response: Response<PayoutsListResponse>) => {
        if (!response?.data) {
          return { data: [], pagination: getDefaultPagination(), requestId: response.requestId };
        }

        return response.data;
      },
      providesTags: [TAGS.PAYOUT_LIST],
    }),
    getPayout: builder.query<PayoutInfoResponse, PayoutQuery>({
      query: ({ accountUuid, payoutUuid, ...rest }) => ({
        url: Endpoint.Payouts.ById(accountUuid, payoutUuid),
        method: HTTPMethod.POST,
        body: rest,
      }),
      providesTags: [TAGS.SINGLE_PAYOUT],
    }),
    retryPayout: builder.mutation<{ nextPayoutUuid: string }, { accountUuid: string; payoutUuid: string }>({
      query: ({ accountUuid, payoutUuid }) => ({
        url: Endpoint.Payouts.Retry.ById(accountUuid, payoutUuid),
        method: HTTPMethod.POST,
        body: {},
      }),
      transformResponse: (response: Response<{ nextSettlementUuid: string }>) => {
        if (!response?.data?.nextSettlementUuid) {
          return { nextPayoutUuid: '' };
        }

        return { nextPayoutUuid: response.data.nextSettlementUuid };
      },
      invalidatesTags: (_, error) => (error ? [] : [TAGS.SINGLE_PAYOUT]),
    }),
  }),
});

export const { useGetPayoutsListQuery, useGetPayoutQuery, useRetryPayoutMutation } = payoutsApi;
export default payoutsApi;
